import { QuestionLinkType } from '@/types/api/question';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getInitials = (name: string, isAnonymous?: boolean) => {
  if (isAnonymous) {
    return 'AN';
  }

  const names = name.split(' ');
  const firstInitial = names[0]?.[0] || '';
  const secondInitial = names.length > 1 ? names[1]?.[0] || '' : '';
  return firstInitial + secondInitial;
};

// Get the base URL from environment variable or fallback to empty string
export const getBaseUrl = () => {
  if (process.env.NEXT_PUBLIC_BASE_URL) {
    return process.env.NEXT_PUBLIC_BASE_URL;
  }

  // Fallback for development
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:3000';
  }

  return 'https://dev.innovationhubsolutions.com';
};

export const generateRandomAvatarColor = (
  name: string
): {
  backgroundColor: string;
  textColor: string;
} => {
  // Define a set of JIRA-like avatar background colors
  const backgroundColors = [
    '#0052CC', // JIRA blue
    '#00875A', // JIRA green
    '#FF5630', // JIRA red (more muted)
    '#FF8B00', // JIRA orange
    '#6554C0', // JIRA purple
    '#008DA6', // JIRA teal
    '#4C9AFF', // JIRA light blue
    '#36B37E', // JIRA light green
    '#FFC400', // JIRA yellow
    '#6B778C', // JIRA gray
    '#403294', // JIRA dark purple
    '#DE350B', // JIRA dark red
    '#00A3BF', // JIRA cyan
    '#5243AA', // JIRA violet
    '#0747A6', // JIRA dark blue
  ];

  // Simple string hash function
  const hashCode = (str: string): number => {
    let hash = 0;
    for (let i = 0; i < str?.length; i++) {
      // Multiply by 31 (prime number) for better distribution
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  };

  // Get a consistent index based on the name
  const index = hashCode(name) % backgroundColors.length;
  const backgroundColor = backgroundColors[index] || '#FFFFFF'; // Fallback to white if no color is found

  // Determine if the background color is dark or light to choose appropriate text color
  // This is a simplified version of calculating luminance
  const r = Number.parseInt(backgroundColor.slice(1, 3), 16);
  const g = Number.parseInt(backgroundColor.slice(3, 5), 16);
  const b = Number.parseInt(backgroundColor.slice(5, 7), 16);

  // Calculate perceived brightness (ITU-R BT.709)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  // Use white text on dark backgrounds, black text on light backgrounds
  const textColor = brightness < 128 ? '#FFFFFF' : '#000000';

  return {
    backgroundColor,
    textColor,
  };
};

export const getRootLink = (type: QuestionLinkType = 'NORMAL') => {
  switch (type) {
    case 'NORMAL':
      return 'home';
    case 'IDEA_STARTER':
      return 'idea-starters';
    case 'DISCUSSIONS':
      return 'discussions';
    case 'TEASER':
      return 'spotlight-questions';
    default:
      return 'home';
  }
};

/**
 * Extract plain text from HTML content for accurate character counting
 * This function removes all HTML tags and returns only the user-typed content
 *
 * @param html - HTML string from rich text editor
 * @returns Plain text string with HTML tags removed
 */
export const extractPlainText = (html: string): string => {
  if (!html) return '';

  // Use regex-based approach that works in both client and server environments
  // This preserves spaces and handles TipTap's HTML output format
  const plainText = html
    .replace(/<br\s*\/?>/gi, '\n') // Convert <br> tags to newlines
    .replace(/<\/p>/gi, '\n') // Convert paragraph endings to newlines
    .replace(/<\/div>/gi, '\n') // Convert div endings to newlines
    .replace(/<li>/gi, '• ') // Convert list items to bullet points
    .replace(/<\/li>/gi, '\n') // Convert list item endings to newlines
    .replace(/<[^>]*>/g, '') // Remove all other HTML tags
    .replace(/&nbsp;/g, ' ') // Convert non-breaking spaces to regular spaces
    .replace(/&amp;/g, '&') // Decode HTML entities
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/&#x2F;/g, '/')
    .replace(/&#x60;/g, '`')
    .replace(/&#x3D;/g, '=');

  return plainText.trim();
};

/**
 * Get character count for plain text content (excluding HTML markup)
 *
 * @param html - HTML string from rich text editor
 * @returns Number of characters in plain text content
 */
export const getPlainTextLength = (html: string): number => {
  const res = extractPlainText(html);
  return res.length;
};
